import {
  OVERLEVEL_XP_DEGRADE_FLOOR,
  OVERLEVEL_XP_DEGRADE_PER_LEVEL,
  OVERLEVEL_XP_HARD_CAP_LEVELS,
} from '@helpers/config';
import { getEntry } from '@helpers/content/content';
import { rangeAtLevel } from '@helpers/engine/leveled-range';
import { rngNumberRange } from '@helpers/rng';
import type {
  EncounterFightMonster,
  MonsterContent,
  MonsterSkill,
  StatBlock,
} from '@interfaces';
import { sortBy } from 'es-toolkit/compat';

export function monsterStatsAtLevel(
  monster: MonsterContent,
  level: number,
): StatBlock {
  const stats = { ...monster.baseStats };

  (Object.keys(stats) as Array<keyof StatBlock>).forEach((stat) => {
    stats[stat] += monster.statsPerLevel[stat] * (level - 1);
  });

  return stats;
}

export function monsterSkillsAtLevel(
  monster: MonsterContent,
  level: number,
): MonsterSkill[] {
  return monster.skills.filter(
    (skill) => skill.minLevel <= level && skill.maxLevel >= level,
  );
}

export function monsterXpReward(
  monster: MonsterContent,
  level: number,
): number {
  const range = rangeAtLevel(monster.xp, level);
  return rngNumberRange(range.min, range.max);
}

export function xpForOverLevel(
  rawXp: number,
  partyLevel: number,
  nodeMaxLevel: number,
): number {
  const levelsOverCap = partyLevel - nodeMaxLevel;
  if (levelsOverCap <= 0) return rawXp;
  if (levelsOverCap >= OVERLEVEL_XP_HARD_CAP_LEVELS) return 0;

  const multiplier = 1 - OVERLEVEL_XP_DEGRADE_PER_LEVEL * levelsOverCap;
  return Math.max(OVERLEVEL_XP_DEGRADE_FLOOR, Math.round(rawXp * multiplier));
}

// Lets a node be judged not worth the trip before any fight happens, rather than only after.
export function isXpTrivialAtOverLevel(
  partyLevel: number,
  nodeMaxLevel: number,
): boolean {
  return partyLevel - nodeMaxLevel >= OVERLEVEL_XP_HARD_CAP_LEVELS;
}

// Shared by authored Encounters and generated ExploreRandom fights; sorted alphabetically for the map node panel's monster tooltip.
export function monstersFromFights(
  fights: Array<{ monsters: EncounterFightMonster[] }>,
): MonsterContent[] {
  const seen = new Set<string>();
  const monsters: MonsterContent[] = [];

  fights.forEach((fight) => {
    fight.monsters.forEach(({ monsterId }) => {
      if (seen.has(monsterId)) return;

      const monster = getEntry<MonsterContent>(monsterId);
      if (!monster) return;

      seen.add(monsterId);
      monsters.push(monster);
    });
  });

  return sortBy(monsters, (monster) => monster.name);
}
